import React, { useEffect, useMemo, useRef, useState } from "react";
import { useMutation } from "@tanstack/react-query";
import { Box, Button, InputLabel, Stack } from "@mui/material";
import dayjs from "dayjs";

import { getSitesEmissions, SitesEmissionsDTO } from "api";
import { useGetAllowedValues, useGetConfigParameter, useNotify, useSites, useUtilityBillsYears } from "hooks";
import { ILoggedUserData } from "types";
import { useNewBreadcrumb } from "recoils";
import { getDateFormatmonthset, getLoggedUserData } from "utils";
import { COLORS, DashboardTabs, EsgDatePickerField, MultiSelectControlledOption, Switch } from "components";
import { EsgMultiselectControlledField } from "components/inputs/esg-multi-select-field";

import { AddDateModal } from "./components";
import { getAllTabs, SITES_EMISSIONS_PAGE, mainTabs } from "./consts";

import { getFiscalYear } from "../documents/missing-documents/helpers";
import { SelectField } from "../documents/missing-documents/components";
import { getSitesFiltered, toggleSelectionValues } from "./helpers";

export const SitesEmissionsPage = () => {
  useNewBreadcrumb(SITES_EMISSIONS_PAGE.TITLE);

  const notify = useNotify();
  const loggedUserData: ILoggedUserData = getLoggedUserData();

  const { data: configParameter } = useGetConfigParameter();
  const { data: availableYears } = useUtilityBillsYears();
  const { data: allowedValues } = useGetAllowedValues();
  const { data: sitesData } = useSites();

  const { mutateAsync: getSitesEmissionsRequest, isLoading: loadingSitesEmissions } = useMutation(getSitesEmissions);

  const isAdminUser = loggedUserData.roles?.some((rol: string) => rol === 'MODULE_ADMIN' || rol === 'SUPER_ADMIN') || false;

  const companyMonths = useMemo(() => (configParameter && configParameter
    .filter((param: any) => param.name.includes("company_month"))
    .map((param: any) => param.configValue)) || ["Jan", "Dec"], [configParameter]);

  const allFiscalYears: string[] = useMemo(() => (availableYears && availableYears
    .map((fy: number) => `${fy} Fiscal Year`)) || [`${dayjs().get("year")} Fiscal Year`], [availableYears]);

  const utilityTypes = useMemo(() => allowedValues && allowedValues.utilityTypes
    .sort((utA: any, utB: any) => (utA.name > utB.name ? 1 : -1))
    .map((ut: any) => ({ name: ut.name, label: ut.name, value: ut.id })) || [], [allowedValues]);

  const sites = useMemo(() => sitesData && sitesData
    .map((site: any) => ({ name: site.name, label: site.name, value: site.id })) || [], [sitesData]);

  const businessUnits = useMemo(() => allowedValues && allowedValues.businessUnits
    .sort().map((bu: string) => ({ name: bu, label: bu, value: bu })) || [], [allowedValues]);

  const countryRegions = useMemo(() => allowedValues && allowedValues.countryRegions
    .sort().map((cr: string) => ({ name: cr, label: cr, value: cr })) || [], [allowedValues]);

  const [viewType, setViewType] = useState<string>("monthly");
  const [isDisaggregated, setIsDisaggregated] = useState<boolean>(true);
  const [currentEmissionsData, setCurrentEmissionsData] = useState<SitesEmissionsDTO | null>(null);
  const [currentFY, setCurrentFY] = useState<string>(allFiscalYears[allFiscalYears.length - 1]);
  const [isSitesFiltered, setIsSitesFiltered] = useState<boolean>(false);
  const [dateError, setDateError] = useState<boolean>(false);

  const [currentUtilityTypeIds, setCurrentUtilityTypeIds] = useState<MultiSelectControlledOption[]>([]);
  const [currentSiteIds, setCurrentSiteIds] = useState<MultiSelectControlledOption[]>([]);
  const [currentBusinessUnits, setCurrentBusinessUnits] = useState<MultiSelectControlledOption[]>([]);
  const [currentCountryRegions, setCurrentCountryRegions] = useState<MultiSelectControlledOption[]>([]);

  const minDate = dayjs(`${Math.min(...(availableYears || [dayjs().year()]))}-01-01`);
  const maxDate = dayjs().endOf("day");

  const fiscalYearData = getFiscalYear(companyMonths, currentFY.substring(0, 4));

  const defaultObject = useRef<any>({
    startDate: dayjs().startOf("month").format("YYYY-MM-DD"),
    endDate: dayjs().format("YYYY-MM-DD"),
    viewType,
    siteIds: sites.map((site: any) => site.value),
    utilityTypeIds: null,
    businessUnits: null,
    countryRegions: null,
    isDisaggregated: String(isDisaggregated),
  });

  const handleChangeIsDisaggregated = (isDisaggregatedValue: boolean) => {
    setIsDisaggregated(isDisaggregatedValue);
    defaultObject.current = {
      ...defaultObject.current,
      isDisaggregated: String(isDisaggregatedValue),
    };
  }

  const allTabs = getAllTabs(
    isAdminUser,
    currentEmissionsData,
    loadingSitesEmissions,
    isDisaggregated,
    handleChangeIsDisaggregated,
    defaultObject.current
  );

  const viewAsDate = (date: string) => {
    defaultObject.current = {
      ...defaultObject.current,
      endDate: dayjs(date).format("YYYY-MM-DD"),
    };
    handleUpdateSitesEmissions();
  }

  const handleSwitchChange = () => {
    const newViewType = viewType === "yearly" ? "monthly" : "yearly";

    let startDate: string;
    let endDate: string;

    if (newViewType === "yearly") {
      startDate = fiscalYearData.fromDate;
      endDate = fiscalYearData.endDate;
    } else {
      startDate = dayjs().startOf("month").format("YYYY-MM-DD");
      endDate = dayjs().format("YYYY-MM-DD");
    }

    setViewType(newViewType);
    defaultObject.current = {
      ...defaultObject.current,
      startDate,
      endDate,
      viewType: newViewType,
    };

    handleUpdateSitesEmissions();
  };

  const handleClearFilters = () => {
    setCurrentFY(allFiscalYears[allFiscalYears.length - 1]);
    setCurrentUtilityTypeIds([]);
    setCurrentSiteIds([]);
    setCurrentBusinessUnits([]);
    setCurrentCountryRegions([]);
    setIsDisaggregated(true);
    setIsSitesFiltered(false);
    defaultObject.current = {
      startDate: dayjs().startOf("month").format("YYYY-MM-DD"),
      endDate: dayjs().format("YYYY-MM-DD"),
      viewType,
      siteIds: sites.map((site: any) => site.value),
      utilityTypeIds: null,
      businessUnits: null,
      countryRegions: null,
      isDisaggregated: String(isDisaggregated),
    };
  };

  const handleDateChange = (date: string, type: string) => {
    if (dayjs(date).isValid()) {
      setDateError(false);
      defaultObject.current = {
        ...defaultObject.current,
        [type]: date,
      };
    } else {
      setDateError(true);
    }
  };

  const handleUpdateSitesEmissions = async () => {
    const sitesEmissionsRequestBody = { ...defaultObject.current };

    await getSitesEmissionsRequest(sitesEmissionsRequestBody, {
      onSuccess: (response: any) => {
        setCurrentEmissionsData(response);
      },
      onError: (error: any) => {
        notify.error(
          typeof error.response?.data === 'string' ? error.response?.data : 'Failed to load Site Emissions Data.'
        );
      },
    });
  };

  useEffect(() => {
    handleUpdateSitesEmissions();
  }, [isDisaggregated]); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <Box sx={{ marginLeft: 3, pt: 1.5 }}>
      <Box
        sx={{
          "@media (max-width: 1100px)": {
            left: "345px",
          },
          "@media (min-width: 1100px)": {
            right: "568px",
          },
          width: "160px",
          "& span:first-child > button": {
            width: "160px",
          },
          position: "absolute",
          top: "-5px",
          right: loadingSitesEmissions ? "500px" : "420px",
          cursor: "pointer",
        }}
      >
        <AddDateModal
          viewAsDate={viewAsDate}
          disabled={viewType === "monthly"}
          year={currentFY}
        />
      </Box>
      <Box sx={{
        width: '100%',
        display: 'flex',
        alignItems: 'center',
        flexWrap: 'wrap',
        gap: 2,
        mb: 2,
      }}>
        <Box sx={{ mt: 3, width: "100px" }}>
          <Switch
            isChecked={viewType === "yearly"}
            label={viewType === "yearly" ? "Year" : "Range"}
            onChange={handleSwitchChange}
          />
        </Box>{viewType === "yearly" && (
          <Box
            sx={{
              width: "200px",
              "& > div:first-of-type": {
                height: "35px",
              },
            }}
          >
            <InputLabel
              sx={{
                fontSize: "12px",
                color: "black",
                fontWeight: 600,
                marginBottom: "4px",
              }}
            >
              Fiscal Year
            </InputLabel>
            <SelectField
              options={allFiscalYears}
              initialValue={currentFY}
              onChangeFn={(event: string) => {
                setCurrentFY(event);
                defaultObject.current = {
                  ...defaultObject.current,
                  fromDate: getFiscalYear(companyMonths, event.substring(0, 4))
                    .fromDate,
                  toDate: getFiscalYear(companyMonths, event.substring(0, 4))
                    .endDate,
                };
              }}
              placeholder="Select Fiscal Year"
            />
          </Box>
        )}
        <Box sx={{ width: "200px" }}>
          <EsgMultiselectControlledField
            label="Utility Type"
            selectedValues={currentUtilityTypeIds}
            setSelectedValues={setCurrentUtilityTypeIds}
            options={utilityTypes}
            type="checkMissing"
            isAllSelectedOnEmptySelection
            onchangeFn={(
              value: string | number,
              unchecked: boolean | undefined,
            ) => {
              defaultObject.current = {
                ...defaultObject.current,
                utilityTypeIds: toggleSelectionValues(currentUtilityTypeIds, value, unchecked),
              };
            }}
          />
        </Box>
        <Box sx={{ width: "200px" }}>
          <EsgMultiselectControlledField
            label="Sites"
            selectedValues={currentSiteIds}
            setSelectedValues={setCurrentSiteIds}
            options={
              !isSitesFiltered
                ? sites
                : getSitesFiltered(sitesData || [], currentCountryRegions)
            }
            type="checkMissing"
            isAllSelectedOnEmptySelection
            onchangeFn={(
              value: string | number,
              unchecked: boolean | undefined,
            ) => {
              defaultObject.current = {
                ...defaultObject.current,
                siteIds:
                  toggleSelectionValues(currentSiteIds, value, unchecked) ??
                  sites.map((site: any) => site.value),
              };
            }}
          />
        </Box>
        <Box sx={{ width: "200px" }}>
          <EsgMultiselectControlledField
            label="Business Unit"
            selectedValues={currentBusinessUnits}
            setSelectedValues={setCurrentBusinessUnits}
            options={businessUnits}
            type="checkMissing"
            isAllSelectedOnEmptySelection
            onchangeFn={(
              value: string | number,
              unchecked: boolean | undefined,
            ) => {
              defaultObject.current = {
                ...defaultObject.current,
                businessUnits: toggleSelectionValues(currentBusinessUnits, value, unchecked),
              };
            }}
          />
        </Box>
        <Box sx={{ width: "200px" }}>
          <EsgMultiselectControlledField
            label="Region"
            selectedValues={currentCountryRegions}
            setSelectedValues={setCurrentCountryRegions}
            options={countryRegions}
            type="checkMissing"
            isAllSelectedOnEmptySelection
            onchangeFn={(
              value: string | number,
              unchecked: boolean | undefined,
            ) => {
              defaultObject.current = {
                ...defaultObject.current,
                countryRegions: toggleSelectionValues(currentCountryRegions, value, unchecked),
              };
            }}
          />
        </Box>
        {viewType === "monthly" && (
          <Stack direction="row" spacing={2}>
            <EsgDatePickerField
              width="200px"
              label="From Month"
              views={["year", "month"]}
              openTo="month"
              minDate={minDate}
              maxDate={maxDate}
              format={getDateFormatmonthset()}
              disableFuture
              disableHighlightToday
              onAccept={(e: any) => handleDateChange(e, "startDate")}
              onChange={(e: any) => handleDateChange(e, "startDate")}
              setDate={(e: any) => handleDateChange(e, "startDate")}
              value={dayjs(defaultObject.current.startDate)}
              placeholdervalue="MM-YYYY"
              filterAll={false}
              clearDate={false}
            />
            <EsgDatePickerField
              width="200px"
              label="To Month"
              views={["year", "month"]}
              openTo="month"
              minDate={minDate}
              maxDate={maxDate}
              format={getDateFormatmonthset()}
              disableFuture
              disableHighlightToday
              onAccept={(e: any) => handleDateChange(e, "endDate")}
              onChange={(e: any) => handleDateChange(e, "endDate")}
              setDate={(e: any) => handleDateChange(e, "endDate")}
              value={dayjs(defaultObject.current.endDate)}
              placeholdervalue="MM-YYYY"
              filterAll={false}
              clearDate={false}
            />
            {dateError && (
              <Box sx={{ color: COLORS.Fuzzy_Brown, fontSize: "0.75rem" }}>
                Invalid date selection. Please choose a date after the From
                Date.
              </Box>
            )}
          </Stack>
        )}
        <Button
          sx={{
            width: "100px",
            paddingTop: "10px",
            border: "1px solid #000",
            marginTop: "22px",
          }}
          onClick={handleUpdateSitesEmissions}
          disabled={dateError}
        >
          Search
        </Button>
        <Button
          sx={{
            width: "120px",
            paddingTop: "10px",
            border: "1px solid #000",
            marginTop: "22px",
          }}
          onClick={handleClearFilters}
        >
          Clear Dates
        </Button>
      </Box>
      <DashboardTabs intensity tabs={allTabs} selectedBtnOption={mainTabs.INITIAL_TAB} dashboard />
    </Box>
  );
};

export default SitesEmissionsPage;
